import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, ChevronDown, Mail } from 'lucide-react';
import { useFamily, useInvites } from '../../hooks';
import { FamilyAvatar } from './FamilyAvatar';
import { useT } from '../../i18n';

// Top-bar family switcher (D8): selecting a family re-scopes every view via
// the persisted currentFamilyIdAtom. The chat list filters on it; the family
// page and admin section read it too.
interface FamilySwitcherProps {
  /** "title": the family's name is the page title (home). "compact": a small
   *  label above another page's title (Documents, Links, Lists). */
  variant?: 'title' | 'compact';
}

export function FamilySwitcher({ variant = 'title' }: FamilySwitcherProps) {
  const {
    families,
    currentFamilyId,
    setCurrentFamilyId,
    loadFamilies,
    hasFamilies,
  } = useFamily();
  const { count: inviteCount } = useInvites();
  const navigate = useNavigate();
  const t = useT();
  const subtle = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.100', 'gray.800');

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  // Recover from a stale persisted id (family left/deleted) or first login.
  useEffect(() => {
    if (families.length === 0) return;
    if (!currentFamilyId || !families.some((f) => f.id === currentFamilyId)) {
      setCurrentFamilyId(families[0]!.id);
    }
  }, [families, currentFamilyId, setCurrentFamilyId]);

  if (!hasFamilies) return null;

  const current =
    families.find((f) => f.id === currentFamilyId) ?? families[0]!;

  return (
    <Menu placement="bottom-start">
      {/* The family's name is the page title (Living Room). */}
      <MenuButton
        aria-label={t('account.switcher.label', { name: current.name })}
        px={variant === 'title' ? 2 : 0}
        h={variant === 'title' ? (current.handle ? '56px' : '48px') : '28px'}
        maxW="full"
        borderRadius="14px"
        color={variant === 'title' ? undefined : 'muted'}
        _hover={{ bg: variant === 'title' ? hoverBg : undefined, color: 'ink' }}
        _expanded={{ bg: variant === 'title' ? hoverBg : undefined }}
      >
        <HStack spacing={variant === 'title' ? 2 : 1} minW={0}>
          <Box minW={0} textAlign="left">
            <Text
              as="span"
              display="block"
              fontFamily={variant === 'title' ? 'heading' : 'body'}
              fontWeight={variant === 'title' ? 800 : 600}
              fontSize={
                variant === 'title' ? { base: '26px', md: '24px' } : '14px'
              }
              letterSpacing={variant === 'title' ? '-0.02em' : undefined}
              lineHeight={1.1}
              noOfLines={1}
            >
              {current.name}
            </Text>
            {/* The handle tells same-named families apart (D217). */}
            {variant === 'title' && current.handle && (
              <Text
                as="span"
                display="block"
                fontSize="13px"
                fontWeight={500}
                color="muted"
                lineHeight={1.2}
                mt="2px"
                noOfLines={1}
              >
                @{current.handle}
              </Text>
            )}
          </Box>
          <Box flexShrink={0}>
            <ChevronDown
              size={variant === 'title' ? 22 : 16}
              color="currentColor"
              aria-hidden="true"
            />
          </Box>
        </HStack>
      </MenuButton>
      <MenuList minW="240px">
        {families.map((family) => (
          <MenuItem
            key={family.id}
            onClick={() => setCurrentFamilyId(family.id)}
            icon={
              <FamilyAvatar
                id={family.id}
                name={family.name}
                src={family.avatarUrl}
                size="xs"
              />
            }
          >
            <HStack flex="1" justify="space-between">
              <Box overflow="hidden">
                <Text fontSize="sm" noOfLines={1}>
                  {family.name}
                </Text>
                <Text fontSize="xs" color={subtle} noOfLines={1}>
                  {family.handle ? `@${family.handle} · ` : ''}
                  {family.memberCount === 1
                    ? t('account.switcher.membersOne')
                    : t('account.switcher.membersOther', {
                        count: family.memberCount,
                      })}
                </Text>
              </Box>
              {family.id === current.id && <Check size={16} />}
            </HStack>
          </MenuItem>
        ))}

        <MenuDivider />
        <MenuItem
          icon={<Mail size={16} />}
          onClick={() => navigate('/invites')}
        >
          <HStack flex="1" justify="space-between">
            <Text>{t('account.switcher.pendingInvites')}</Text>
            {inviteCount > 0 && (
              <Badge colorScheme="red" borderRadius="full">
                {inviteCount}
              </Badge>
            )}
          </HStack>
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
